import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useExpectedUploadedDocuments } from '@folio/utils/useExpectedUploadedDocuments';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DocumentsListsPolicyModule,
  type DocumentConformityState,
} from '../DocumentsListsPolicyModule/DocumentsListsPolicyModule';
import { DocumentsUploadPolicyModule } from '../DocumentsUploadPolicyModule/DocumentsUploadPolicyModule';
import { Stack } from '@folio/components/ui/flex';

type PolicyDocumentsSectionsProps = {
  displayContext: 'broker' | 'policyholder';
  excludedCategories?: string[];
  generatedDocumentsActions?: ReactNode;
  conformityByDocumentId?: Map<string, DocumentConformityState>;
  onReuploadDocumentConformity?: (documentId: string, file: File) => void;
};

export function PolicyDocumentsSections({
  displayContext,
  excludedCategories,
  generatedDocumentsActions,
  conformityByDocumentId,
  onReuploadDocumentConformity,
}: PolicyDocumentsSectionsProps) {
  const { t } = useTranslation();
  const { policy, branchIdForPolicy } = usePolicyOnDashboard();
  const [showArchivedDocuments, setShowArchivedDocuments] = useState(false);

  const {
    data: policyholderDocuments,
    isPending,
    isError,
  } = useExpectedUploadedDocuments({
    productId: policy.productId,
    policyId: policy.id,
    branchId: branchIdForPolicy,
  });

  const contractExcludedCategories = useMemo(
    () => [
      ...new Set([
        ...(excludedCategories ?? []),
        ...(policyholderDocuments?.map(({ category }) => category) ?? []),
      ]),
    ],
    [excludedCategories, policyholderDocuments],
  );

  return (
    <Stack gap={6}>
      {isPending && <p role="status">{t('documentReview.loading')}</p>}
      {isError && !policyholderDocuments && <p role="alert">{t('documentReview.loadError')}</p>}
      {policyholderDocuments && policyholderDocuments.length > 0 && (
        <DocumentsUploadPolicyModule
          displayContext={displayContext}
          reviewLayout
          documentsLists={policyholderDocuments}
          useBranchId={Boolean(branchIdForPolicy)}
          branchId={branchIdForPolicy}
          conformityByDocumentId={conformityByDocumentId}
          onReuploadDocumentConformity={onReuploadDocumentConformity}
        />
      )}
      <section className="overflow-hidden rounded-lg border bg-card">
        <h3 className="border-b px-5 py-4 text-lg font-medium leading-none tracking-tight text-secondary md:text-xl">
          {t('documentReview.contractDocuments')}
        </h3>
        <DocumentsListsPolicyModule
          displayContext={displayContext}
          embedded
          excludedCategories={contractExcludedCategories}
          includeUnconfiguredDocuments
          showArchivedDocuments={showArchivedDocuments}
          onShowArchivedDocumentsChange={setShowArchivedDocuments}
          extraActions={displayContext === 'broker' ? generatedDocumentsActions : undefined}
        />
      </section>
    </Stack>
  );
}
